import PropTypes from 'prop-types';
import { m } from 'framer-motion';
import { useState, useEffect } from 'react';
import { NavLink as RouterLink, useLocation } from 'react-router-dom';
// @mui
import { styled } from '@mui/material/styles';
import {
  Box,
  Link,
  Grid,
  List,
  Stack,
  Popover,
  ListItem,
  ListSubheader,
  CardActionArea,
  Typography,
} from '@mui/material';
// components
import styles from '../../styles/layouts/main/MenuDesktop.module.css';
import Iconify from '../../components/Iconify';
import { PATH_AUTH, PATH_FEATURED, PATH_PAGE } from '../../routes/paths';
// ----------------------------------------------------------------------

const LinkStyle = styled(Link)(({ theme }) => ({
  ...theme.typography.subtitle2,
  color: '#0275B5',
  marginRight: theme.spacing(3),
  transition: theme.transitions.create('opacity', {
    duration: theme.transitions.duration.shorter,
  }),
  '&:hover': {
    opacity: 0.48,
    textDecoration: 'none',
  },
}));

// ----------------------------------------------------------------------

MenuDesktopCPO.propTypes = {
  isHome: PropTypes.bool,
  isOffset: PropTypes.bool,
  navConfig: PropTypes.array,
};

export default function MenuDesktopCPO({ isOffset, isHome, navConfig }) {
  // NOTE: per-item popovers now manage their own open state; no shared state here
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div className={styles.top_menu_text}>Bridging Donors to Worthy Causes</div>
      <Stack direction="row" style={{ marginLeft: 24 }}>
        {navConfig.map(
          (link) =>
            link.title !== 'PROFILE' && (
              <MenuDesktopItem key={link.title} item={link} isOffset={isOffset} isHome={isHome} />
            )
        )}
      </Stack>
    </div>
  );
}

// ----------------------------------------------------------------------

IconBullet.propTypes = {
  type: PropTypes.oneOf(['item', 'subheader']),
};

function IconBullet({ type = 'item' }) {
  return (
    <Box sx={{ width: 24, height: 16, display: 'flex', alignItems: 'center' }}>
      <Box
        component="span"
        sx={{
          ml: '2px',
          width: 4,
          height: 4,
          borderRadius: '50%',
          bgcolor: 'currentColor',
          ...(type !== 'item' && { ml: 0, width: 8, height: 2, borderRadius: 2 }),
        }}
      />
    </Box>
  );
}

// ----------------------------------------------------------------------

MenuDesktopItem.propTypes = {
  isHome: PropTypes.bool,
  isOffset: PropTypes.bool,
  item: PropTypes.shape({
    path: PropTypes.string,
    title: PropTypes.string,
    children: PropTypes.array,
  }),
  anchorEl: HTMLElement,
};

function MenuDesktopItem({ item, isHome, isOffset }) {
  const { pathname } = useLocation();
  const { title, path, children } = item;

  // per-item popover state (separated)
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    if (open) {
      setOpen(false);
      setAnchorEl(null);
    }
    // close on route change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleOpen = (event) => {
    setOpen(true);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(false);
    setAnchorEl(null);
  };

  // Explicit children lists per spec — separate calls
  const JOINUS_LINKS = [
    { path: PATH_AUTH.registerIndividual, title: 'INDIVIDUAL DONOR' },
    { path: PATH_AUTH.registerCorporate, title: 'CORPORATE DONOR' },
    { path: PATH_AUTH.registerVolunteer, title: 'VOLUNTEER' },
    { path: PATH_AUTH.registerNonprofit, title: 'NONPROFIT/PROPONENT' },
  ];

  const SUCCESS_STORIES_LINKS = [
    { path: PATH_PAGE.SuccessStories, title: 'SUCCESS STORIES' },
    // { path: PATH_FEATURED.projectAkap, title: 'PROJECT AKAP' },
    // { path: PATH_FEATURED.volunteering, title: 'VOLUNTEERING' },
    // { path: PATH_FEATURED.operationSmallComforts, title: 'OPERATION SMALL COMFORTS' },
    // { path: PATH_FEATURED.screensOfHope, title: 'SCREENS OF HOPE' },
  ];

  // decide which children to render
  const getMenuChildren = () => {
    if (title === 'JOIN US') return JOINUS_LINKS;
    if (title === 'SUCCESS STORIES') return SUCCESS_STORIES_LINKS;
    // fallback to provided config structure (children[0].items)
    if (children && children[0] && Array.isArray(children[0].items)) {
      return children[0].items.map(({ path, title }) => ({ path, title }));
    }
    return [];
  };

  const menuChildren = getMenuChildren();

  if (menuChildren.length) {
    return (
      <>
        <LinkStyle
          onClick={handleOpen}
          sx={{
            display: 'flex',
            cursor: 'pointer',
            alignItems: 'center',
            ...(isHome && { color: '#0275B5' }),
            ...(isOffset && { color: '#0275B5' }),
            ...(open && { opacity: 0.48 }),
          }}
        >
          {title}
          <Iconify
            icon={open ? 'eva:arrow-ios-upward-fill' : 'eva:arrow-ios-downward-fill'}
            sx={{ ml: 0.5, width: 16, height: 16 }}
          />
        </LinkStyle>

        <Popover
          open={open}
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          onClose={handleClose}
          placement="left-start"
          PaperProps={{
            sx: {
              py: 2,
              px: 3,
              mt: 2.5,
              borderRadius: 0.7,
              width: 300,
              boxShadow: (theme) => theme.customShadows.z24,
            },
          }}
        >
          <List disablePadding>
            {menuChildren.map((link, idx) => (
              <Box key={`${title}-${idx}`} sx={{ my: 0.8 }}>
                <LinkStyle
                  href={link.path}
                  target="_self"
                  rel="noopener"
                  sx={{ color: '#0275B5', my: 3 }}
                  onClick={handleClose}
                >
                  {link.title}
                </LinkStyle>
              </Box>
            ))}
          </List>
        </Popover>
      </>
    );
  }

  if (title === 'Documentation') {
    return (
      <LinkStyle
        href={path}
        target="_blank"
        rel="noopener"
        sx={{
          ...(isHome && { color: 'common.white' }),
          ...(isOffset && { color: 'text.primary' }),
        }}
      >
        {title}
      </LinkStyle>
    );
  }

  return (
    <LinkStyle
      to={path}
      component={RouterLink}
      end={path === '/'}
      className={styles.top_menu_link_underline}
      sx={{
        ...(isHome && { color: '#0275B5' }),
        ...(isOffset && { color: '#0275B5' }),
      }}
    >
      {title}
    </LinkStyle>
  );
}
